import React from "react";
import { Tooltip } from "antd";
import {
  getActionCategory,
  getActionLabel,
  getRoleBadge,
  formatRelativeTime,
  groupEventsByDate,
} from "./activityLogConstants";
import { formatDdMmYyyyTime } from "../utils/formatDdMmYyyy";
import { displayEmail } from '../utils/displayEmail';

function RoleBadge({ role }) {
  const badge = getRoleBadge(role);
  return (
    <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[8px] font-black ${badge.color}`}>
      {badge.label}
    </span>
  );
}

function EventNode({ event }) {
  const cat = getActionCategory(event.action);
  const label = getActionLabel(event.action);
  const actorEmail = displayEmail(event.actor?.email) || "System";
  const actorName = actorEmail.split("@")[0];
  const relTime = formatRelativeTime(event.timestamp);
  const absTime = event.timestamp ? formatDdMmYyyyTime(event.timestamp) : "";

  return (
    <div className="group relative flex gap-3 pb-4 last:pb-0">
      <div className="flex flex-col items-center">
        <div className={`h-2.5 w-2.5 rounded-full ${cat.dotClass} ring-2 ring-[color:var(--color-bg-white)] shrink-0 mt-1.5`} />
        <div className="w-px flex-1 bg-[color:var(--color-bg-alt)] group-last:bg-transparent" />
      </div>
      <div className="min-w-0 flex-1 pb-1">
        <div className="flex items-center gap-2 text-[12px]">
          <RoleBadge role={event.actor?.role} />
          <span className="font-semibold text-[color:var(--color-text-hero)] truncate">{actorName}</span>
          <span className="text-[color:var(--color-text-muted)] truncate flex-1">{label}</span>
          <Tooltip title={absTime}>
            <span className="shrink-0 text-[10px] font-medium text-[color:var(--color-text-muted)]">{relTime}</span>
          </Tooltip>
        </div>
        {event.details ? (
          <p className="mt-0.5 text-[11px] leading-snug text-[color:var(--color-text-muted)] line-clamp-2 pl-7">
            {event.details}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Vertical timeline for audit events, grouped by date.
 */
export function ActivityLogTimeline({ events, loading }) {
  if (loading && (!events || events.length === 0)) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-[color:var(--color-border-strong)] border-t-[color:var(--color-accent-fill)]" />
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <p className="py-8 text-center text-xs font-medium text-[color:var(--color-text-muted)]">
        No activity recorded yet for this section.
      </p>
    );
  }

  const groups = groupEventsByDate(events);

  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <div key={group.label}>
          <div className="mb-2 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
            {group.label}
          </div>
          <div>
            {group.items.map((ev) => (
              <EventNode key={ev._id || ev.id || `${ev.timestamp}-${ev.action}`} event={ev} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
